import React, { useState } from "react";
import { Switch } from "@shared/ui/switch";
import { Calendar } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { MeterReadingDetail } from "../../types";

interface ReadingDetailsCardProps {
  detail: MeterReadingDetail;
  className?: string;
  showPreviousData?: boolean;
  onShowPreviousDataChange?: (show: boolean) => void;
}

export const ReadingDetailsCard: React.FC<ReadingDetailsCardProps> = ({
  detail,
  className,
  showPreviousData: controlledShowPreviousData,
  onShowPreviousDataChange,
}) => {
  const [internalShowPrevious, setInternalShowPrevious] = useState(true);
  const showPreviousData =
    controlledShowPreviousData !== undefined
      ? controlledShowPreviousData
      : internalShowPrevious;
  const setShowPreviousData =
    onShowPreviousDataChange || setInternalShowPrevious;
  const rd = detail?.readingData;

  return (
    <div className={cn("bg-white border rounded-lg p-6 space-y-6 shadow-sm", className)}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Reading Details</h2>
        <div className="flex items-center gap-2 space-between">
          <Switch
            checked={showPreviousData}
            onCheckedChange={setShowPreviousData}
            id="show-previous"
          />
          <label htmlFor="show-previous" className="text-sm text-blue-600 font-medium cursor-pointer">
            Show Previous Data
          </label>
        </div>
      </div>

      {/* Top 3 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Previous Reading */}
        <QuickStatsCard
          title="Previous Reading"
          displayValue={rd?.previousReading ?? "NA"}
          trendSummary={
            <div className="flex items-center gap-1.5 text-grey-600 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              <span>{rd?.previousReadingDate ?? "NA"}</span>
            </div>
          }
          badge={rd?.previousStatus ?? "NA"}
          badgeColor="bg-green-50 text-green-700 border border-green-200"
          className="bg-gray-50/80 shadow-none border"
        />

        {/* Current Reading */}
        <QuickStatsCard
          title="Current Reading"
          displayValue={rd?.currentReading ?? "NA"}
          trendSummary={
            <div className="flex items-center gap-1.5 text-grey-600 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              <span>{rd?.currentReadingDate ?? "NA"}</span>
            </div>
          }
          badge={rd?.currentStatus ?? "NA"}
          badgeColor="bg-green-50 text-green-700 border border-green-200"
          className="bg-gray-50/80 shadow-none border"
        />

        {/* Consumption */}
        <QuickStatsCard
          title="Consumption"
          displayValue={<span className="text-blue-600">{rd?.consumption ?? "NA"}</span>}
          trendSummary="units consumed"
          badge="Between readings"
          badgeColor="bg-blue-50 text-blue-700 border border-blue-200"
          className="bg-gray-50/80 shadow-none border"
        />
      </div>

      {/* Meter Reader Remark */}
      <div className="bg-gray-50/80 rounded-lg p-4 flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-400 tracking-wider">Meter Reader's Remark</span>
        <span className="text-gray-900 font-medium">{detail?.remarks ?? "NA"}</span>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 pt-2">
        {Object.values(detail?.meterCycleDetails?.readFormatConfiguration || {})
          .sort((a: any, b: any) => (a.sequence_id || 0) - (b.sequence_id || 0))
          .map((field: any) => {
            const currentVal = detail?.readingData?.additionalData?.values?.[field.key] ?? "NA";
            const prevVal = detail?.readingData?.previousAdditionalData?.values?.[field.key] ?? "NA";
            
            return (
              <ValueLabel
                key={field.key}
                label={field.fieldName}
                value={currentVal}
                description={showPreviousData ? `Prev: ${prevVal}` : null}
              />
            );
          })}
      </div>
    </div>
  );
};
